import { BarsArrowDownIcon, BarsArrowUpIcon } from '@heroicons/react/20/solid';
import ButtonV2 from '../button/ButtonV2';
import { useEffect, useState } from 'react';
import { Popover } from '@headlessui/react';
import { useSearchParams } from 'react-router-dom';
import * as qs from 'qs';
import { AnimatePresence, motion } from 'framer-motion';

import { FinalizedQuery, SortDirection, SortOption } from './sort.types';
import SortMenu from './SortMenu';

export type SortV2Props = {
    size?: 'xs' | 'sm';
    options: SortOption[];
    defaultDirection?: SortDirection;
    defaultValue?: string;
    onChange: (vals: { query: FinalizedQuery; queryString: string }) => void;
};

function sanitizeSort(
    query: qs.ParsedQs,
    options: SortOption[],
    defaultDirection?: SortDirection,
    defaultValue?: string
): { dir: SortDirection; field: string } {
    let _default = defaultValue;
    // Check if field is in list
    if (defaultValue && !options.find((o) => o.value === defaultValue)) {
        _default = undefined;
    }

    const sort = query.sort || { dir: defaultDirection || 'asc', field: _default };

    let _dir: SortDirection = defaultDirection || 'asc';
    let _field = defaultValue || '';

    if (typeof sort === 'string' || Array.isArray(sort)) {
        return { dir: defaultDirection || 'asc', field: _default || options[0].value };
    }

    const { dir, field } = sort;

    // Clean sort direction
    if (dir === 'asc' || dir === 'desc') {
        _dir = dir;
    } else {
        _dir = defaultDirection || 'asc';
    }

    // Clean option
    if (typeof field === 'string') {
        _field = options.find((o) => o.value === field)?.value || _default || options[0].value;
    } else {
        _field = _default || options[0].value;
    }

    return { dir: _dir, field: _field };
}

function SortV2({ size = 'sm', options, defaultDirection, defaultValue, onChange }: SortV2Props): JSX.Element {
    const [search, setSearch] = useSearchParams();

    function initSettings(): { dir: SortDirection; field: string } {
        return sanitizeSort(qs.parse(search.toString()), options, defaultDirection, defaultValue);
    }

    const [query, setQuery] = useState(() => initSettings());

    function handleDirectionChange(dir: SortDirection): void {
        setQuery((s) => ({ ...s, dir }));
    }

    function handleFieldChange(field: string): void {
        setQuery((s) => ({ ...s, field }));
    }

    function commit(): void {
        search.set('sort[dir]', query.dir);
        search.set('sort[field]', query.field);
        setSearch(search, { replace: true });
        onChange({
            query,
            queryString: `sort[dir]=${query.dir}&sort[field]=${query.field}`,
        });
    }

    // When search changes
    useEffect(() => {
        const url = qs.parse(search.toString());

        const newQuery = sanitizeSort(url, options, query.dir, query.field);

        // @ts-ignore
        if (
            newQuery.dir !== query.dir ||
            newQuery.field !== query.field ||
            // @ts-ignore
            url.sort?.dir !== newQuery.dir ||
            // @ts-ignore
            url.sort?.field !== newQuery.field
        ) {
            search.set('sort[dir]', newQuery.dir);
            search.set('sort[field]', newQuery.field);
            setSearch(search, { replace: true });
            setQuery(newQuery);
            onChange({
                query,
                queryString: `sort[dir]=${newQuery.dir}&sort[field]=${newQuery.field}`,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        /**
         * When options change:
         *  - Check if the current url has that option, if it does exit
         *  - Else, set the url to the first match
         */
        const url = qs.parse(search.toString());
        const newQuery = sanitizeSort(url, options, query.dir, query.field);

        // @ts-ignore
        if (
            newQuery.dir !== query.dir ||
            newQuery.field !== query.field ||
            // @ts-ignore
            url.sort?.dir !== newQuery.dir ||
            // @ts-ignore
            url.sort?.field !== newQuery.field
        ) {
            search.set('sort[dir]', newQuery.dir);
            search.set('sort[field]', newQuery.field);
            setSearch(search, { replace: true });
            setQuery(newQuery);
            onChange({
                query,
                queryString: `sort[dir]=${newQuery.dir}&sort[field]=${newQuery.field}`,
            });
        }
    }, [options]);

    // useEffect(() => {
    //     const url = sanitizeSort(qs.parse(search.toString()), options, defaultDirection, defaultValue);
    //     setSettings({ dir: url.dir, field: url.field });
    //     search.set('sort[dir]', url.dir);
    //     search.set('sort[field]', url.field);
    //     setSearch(search, { replace: true });
    //     onChange({
    //         query: {
    //             dir: url.dir,
    //             field: url.field,
    //         },
    //         queryString: `sort[dir]=${url.dir}&sort[field]=${url.field}`,
    //     });
    // }, [search, options, setSearch, onChange, defaultDirection, defaultValue]);

    return (
        <Popover className="w-fit">
            {({ open }): JSX.Element => {
                return (
                    <>
                        <Popover.Button as="div">
                            <ButtonV2
                                size={size}
                                type="secondary"
                                label={options?.find((o) => o.value === query.field)?.label || 'Sort'}
                                leftIcon={query.dir === 'asc' ? <BarsArrowUpIcon /> : <BarsArrowDownIcon />}
                            />
                        </Popover.Button>
                        <AnimatePresence initial={false} onExitComplete={commit}>
                            {open && (
                                <Popover.Panel
                                    className="bg-white shadow-md rounded-md min-w-max w-fit outline-none absolute mt-2 z-50 dark:border-white/[0.05] dark:text-gray-200 dark:bg-white/[0.05] p-4"
                                    as={motion.div}
                                    static
                                >
                                    <SortMenu
                                        options={options}
                                        dir={query.dir}
                                        field={query.field}
                                        onDirectionChange={handleDirectionChange}
                                        onOptionChange={handleFieldChange}
                                    />
                                </Popover.Panel>
                            )}
                        </AnimatePresence>
                    </>
                );
            }}
        </Popover>
    );
}

export default SortV2;
